/**
 * Utilitario de construcao de URLs de imagens para o cardapio digital.
 * Centraliza a logica que estava no metodo processarResposta() do componente principal.
 */

import { CardapioEstabelecimentoDTO, CardapioCategoriaDTO } from '../../../models/cardapio-digital/cardapio-digital.model';

/**
 * Constroi URL completa de uma imagem a partir do nome do arquivo e prefixo.
 * Se a imagem ja for uma URL completa (http/https), retorna como esta.
 * @param imagem - Nome do arquivo ou URL completa
 * @param prefixo - Prefixo base para construir a URL (ex: 'https://img.host/123/item')
 * @returns URL completa ou null se imagem vazia
 */
export function construirUrlImagem(imagem: string | null | undefined, prefixo: string): string | null {
  if (!imagem || !imagem.trim()) return null;
  if (imagem.startsWith('http')) return imagem;
  return `${prefixo}/${imagem}`;
}

/**
 * Processa todas as URLs de imagens do estabelecimento (banner, imagemDeTitulo, logo).
 * Modifica o objeto in-place.
 *
 * Os campos banner e imagemDeTitulo representam a mesma imagem (minishopImagemDeTitulo do backend).
 * Se apenas um deles vier preenchido, o outro e sincronizado para garantir exibicao correta.
 */
export function processarImagensEstabelecimento(
  estabelecimento: CardapioEstabelecimentoDTO,
  prefixo: string,
): void {
  if (estabelecimento.banner) {
    const url = construirUrlImagem(estabelecimento.banner, prefixo);
    if (url) estabelecimento.banner = url;
  }
  if (estabelecimento.imagemDeTitulo) {
    const url = construirUrlImagem(estabelecimento.imagemDeTitulo, prefixo);
    if (url) estabelecimento.imagemDeTitulo = url;
  }
  if (estabelecimento.logo) {
    const url = construirUrlImagem(estabelecimento.logo, prefixo);
    if (url) estabelecimento.logo = url;
  }

  // Sincronizar banner <-> imagemDeTitulo: se apenas um dos dois estiver preenchido
  // apos o processamento, copiar para o outro campo para garantir exibicao consistente.
  // Isso protege contra inconsistencias no backend onde apenas um dos campos e retornado.
  if (estabelecimento.banner && !estabelecimento.imagemDeTitulo) {
    estabelecimento.imagemDeTitulo = estabelecimento.banner;
  } else if (estabelecimento.imagemDeTitulo && !estabelecimento.banner) {
    estabelecimento.banner = estabelecimento.imagemDeTitulo;
  }
}

/**
 * Processa todas as URLs de imagens dos produtos nas categorias.
 * Modifica os objetos in-place.
 */
export function processarImagensProdutos(
  categorias: CardapioCategoriaDTO[],
  prefixo: string,
): void {
  categorias.forEach(cat => {
    cat.produtos.forEach(prod => {
      if (prod.foto) {
        const url = construirUrlImagem(prod.foto, prefixo);
        if (url) prod.foto = url;
      }
    });
  });
}
